import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Input, Select, Tag, Button, Space, Spin, Alert, Drawer, Descriptions, theme } from 'antd';
import { message } from '../../../services/toastMessage';
import { IconCalendarEvent, IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  getAdminBookings,
  getAdminBooking,
  forceCancelAdminBooking,
  IAdminBookingSummary,
  IAdminBookingDetail,
} from '../../../services/AdminService';
import EmptyState from '../../../components/EmptyState';
import AdminReasonModal from '../../../components/Admin/AdminReasonModal';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import BookingMoneyDrawer from '../../../components/Admin/BookingMoneyDrawer';
import {
  CAPTURE_MODE_OPTIONS,
  CAPTURE_MODE_LABEL_KEYS,
  MONEY_LOCATION_LABEL_KEYS,
  MONEY_LOCATION_COLORS,
} from '../../../constants/adminMoneyOptions';
import { BOOKING_STATUS_COLORS, BOOKING_STATUS_LABEL_KEYS } from '../../../constants/bookingStatusOptions';
import { BookingStatus } from '../../../interfaces/heavyDutyMachinery/IBooking';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, numberSorter, dateSorter, enumColumnFilters } from '../../../helpers/adminTableHelpers';

const { Title } = Typography;
const { Search } = Input;

const STATUS_OPTIONS: BookingStatus[] = ['Pending', 'Confirmed', 'Completed', 'Cancelled', 'HostNoShow', 'GuestNoShow'];
const PAYMENT_STATUS_OPTIONS = ['RequiresPayment', 'Succeeded', 'Failed', 'PartiallyRefunded', 'Refunded'];
const CANCELLABLE_STATUSES = ['Pending', 'Confirmed'];

// Booking table filterable by status, full detail, and a forced cancellation
// (bypasses the space's own CancellationPolicy window, unlike the guest's
// self-service cancel) with a recorded reason.
const BookingsSection: React.FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const [bookings, setBookings] = useState<IAdminBookingSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [captureMode, setCaptureMode] = useState<string | undefined>(undefined);
  const [moneyBookingId, setMoneyBookingId] = useState<string | undefined>(undefined);

  const [detail, setDetail] = useState<IAdminBookingDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cancelTargetId, setCancelTargetId] = useState<string | undefined>(undefined);

  const loadBookings = useCallback(() => {
    setLoading(true);
    setError(false);
    getAdminBookings(status, search, captureMode)
      .then(setBookings)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [status, search, captureMode]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const openDetail = (id: string) => {
    setDetailLoading(true);
    setDetail({} as IAdminBookingDetail);
    getAdminBooking(id)
      .then(setDetail)
      .catch(() => {
        message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
        setDetail(null);
      })
      .finally(() => setDetailLoading(false));
  };

  const handleForceCancel = async (reason: string) => {
    if (!cancelTargetId) return;
    try {
      await forceCancelAdminBooking(cancelTargetId, reason);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setCancelTargetId(undefined);
      loadBookings();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  // Shared by the desktop "Acciones" column and the mobile card footer, so
  // neither surface can quietly drift from the other.
  const renderActions = (record: IAdminBookingSummary) => (
    <Space>
      <Button size="small" onClick={() => openDetail(record.id)}>
        {translate(t, TRANSLATIONS.ADMIN_BOOKINGS_VIEW_BUTTON)}
      </Button>
      <Button size="small" onClick={() => setMoneyBookingId(record.id)}>
        {translate(t, TRANSLATIONS.ADMIN_BOOKINGS_MONEY_BUTTON)}
      </Button>
      {CANCELLABLE_STATUSES.includes(record.status) && (
        <Button size="small" danger onClick={() => setCancelTargetId(record.id)}>
          {translate(t, TRANSLATIONS.ADMIN_BOOKINGS_FORCE_CANCEL_BUTTON)}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <Title level={4} style={{ marginBottom: 20 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_BOOKINGS)}
      </Title>

      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_SEARCH_PLACEHOLDER)}
          style={{ maxWidth: 280, width: '100%' }}
          onSearch={(value) => setSearch(value || undefined)}
        />
        <Select
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_STATUS_FILTER_ALL)}
          style={{ maxWidth: 200, width: '100%' }}
          value={status}
          onChange={setStatus}
          options={STATUS_OPTIONS.map((value) => ({ value, label: translate(t, BOOKING_STATUS_LABEL_KEYS[value]) }))}
        />
        <Select
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_CAPTURE_MODE_FILTER_ALL)}
          style={{ maxWidth: 220, width: '100%' }}
          value={captureMode}
          onChange={setCaptureMode}
          options={CAPTURE_MODE_OPTIONS.map((value) => ({ value, label: translate(t, CAPTURE_MODE_LABEL_KEYS[value]) }))}
        />
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_LOAD_ERROR)} />
      ) : bookings.length === 0 ? (
        <EmptyState
          icon={<IconCalendarEvent size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminBookingSummary>
          rowKey="id"
          dataSource={bookings}
          pagination={{ pageSize: 20 }}
          toCard={(record) => ({
            key: record.id,
            title: record.heavyDutyMachineryTitle,
            subtitle: `${record.reference} · ${record.hostName} → ${record.guestName}`,
            badge: (
              <Tag color={BOOKING_STATUS_COLORS[record.status as BookingStatus]} style={{ marginInlineEnd: 0 }}>
                {translate(t, BOOKING_STATUS_LABEL_KEYS[record.status as BookingStatus])}
              </Tag>
            ),
            fields: [
              {
                label: translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_MONEY),
                value: (
                  <Tag color={MONEY_LOCATION_COLORS[record.moneyLocation]} style={{ marginInlineEnd: 0 }}>
                    {translate(t, MONEY_LOCATION_LABEL_KEYS[record.moneyLocation])}
                  </Tag>
                ),
              },
              {
                label: translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_CAPTURE_MODE),
                value: translate(t, CAPTURE_MODE_LABEL_KEYS[record.captureMode] ?? record.captureMode),
              },
              {
                label: translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_DATES),
                value: `${new Date(record.startAt).toLocaleString()} → ${new Date(record.endAt).toLocaleString()}`,
                wide: true,
              },
              { label: translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_TOTAL), value: `${record.totalPrice.toFixed(2)} €` },
              { label: translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_PAYMENT), value: record.paymentStatus },
            ],
            actions: renderActions(record),
          })}
          // Same as UsersSection: horizontal scroll within the table's own
          // container instead of squeezing 8 columns illegibly or breaking
          // the page layout -- fine for an internal admin tool.
          scroll={{ x: 'max-content' }}
          columns={[
            {
              title: translate(t, TRANSLATIONS.BOOKING_REFERENCE_LABEL),
              dataIndex: 'reference',
              key: 'reference',
              sorter: textSorter<IAdminBookingSummary>('reference'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_SPACE),
              dataIndex: 'heavyDutyMachineryTitle',
              key: 'heavyDutyMachineryTitle',
              sorter: textSorter<IAdminBookingSummary>('heavyDutyMachineryTitle'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_HOST),
              dataIndex: 'hostName',
              key: 'hostName',
              sorter: textSorter<IAdminBookingSummary>('hostName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_GUEST),
              dataIndex: 'guestName',
              key: 'guestName',
              sorter: textSorter<IAdminBookingSummary>('guestName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_DATES),
              key: 'dates',
              sorter: dateSorter<IAdminBookingSummary>('startAt'),
              render: (_: unknown, record: IAdminBookingSummary) =>
                `${new Date(record.startAt).toLocaleString()} → ${new Date(record.endAt).toLocaleString()}`,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_TOTAL),
              dataIndex: 'totalPrice',
              key: 'totalPrice',
              sorter: numberSorter<IAdminBookingSummary>('totalPrice'),
              render: (value: number) => `${value.toFixed(2)} €`,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_STATUS),
              dataIndex: 'status',
              key: 'status',
              filters: enumColumnFilters(STATUS_OPTIONS, (value) => translate(t, BOOKING_STATUS_LABEL_KEYS[value as BookingStatus])),
              onFilter: (value, record: IAdminBookingSummary) => record.status === value,
              render: (value: BookingStatus) => <Tag color={BOOKING_STATUS_COLORS[value]}>{translate(t, BOOKING_STATUS_LABEL_KEYS[value])}</Tag>,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_PAYMENT),
              dataIndex: 'paymentStatus',
              key: 'paymentStatus',
              filters: enumColumnFilters(PAYMENT_STATUS_OPTIONS, (value) => value),
              onFilter: (value, record: IAdminBookingSummary) => record.paymentStatus === value,
            },
            {
              // Where the money physically is, not merely whether it was
              // charged -- the same field the money trail leads with, so the
              // two can never read differently.
              title: translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_MONEY),
              key: 'moneyLocation',
              render: (_: unknown, record: IAdminBookingSummary) => (
                <Tag color={MONEY_LOCATION_COLORS[record.moneyLocation]} style={{ marginInlineEnd: 0 }}>
                  {translate(t, MONEY_LOCATION_LABEL_KEYS[record.moneyLocation])}
                </Tag>
              ),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_CAPTURE_MODE),
              dataIndex: 'captureMode',
              key: 'captureMode',
              filters: enumColumnFilters(CAPTURE_MODE_OPTIONS, (value) => translate(t, CAPTURE_MODE_LABEL_KEYS[value])),
              onFilter: (value, record: IAdminBookingSummary) => record.captureMode === value,
              render: (value: string) => translate(t, CAPTURE_MODE_LABEL_KEYS[value] ?? value),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_ACTIONS),
              key: 'actions',
              render: (_: unknown, record: IAdminBookingSummary) => renderActions(record),
            },
          ]}
        />
      )}

      <Drawer
        title={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_DETAIL_TITLE)}
        open={detail !== null}
        onClose={() => setDetail(null)}
        width={480}
      >
        {detailLoading || !detail?.id ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_SPACE)}>{detail.heavyDutyMachineryTitle}</Descriptions.Item>
            <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_HOST)}>{detail.hostName}</Descriptions.Item>
            <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_GUEST)}>{detail.guestName}</Descriptions.Item>
            <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_DATES)}>
              {new Date(detail.startAt).toLocaleString()} → {new Date(detail.endAt).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_TOTAL)}>{detail.totalPrice.toFixed(2)} €</Descriptions.Item>
            <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_STATUS)}>
              <Tag color={BOOKING_STATUS_COLORS[detail.status as BookingStatus]}>
                {translate(t, BOOKING_STATUS_LABEL_KEYS[detail.status as BookingStatus])}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_COL_PAYMENT)}>{detail.paymentStatus}</Descriptions.Item>
            {detail.paymentReference && (
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_DETAIL_PAYMENT_REF_LABEL)}>
                {detail.paymentReference}
              </Descriptions.Item>
            )}
            {detail.hostPayoutAmount != null && (
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_DETAIL_PAYOUT_LABEL)}>
                {detail.hostPayoutAmount.toFixed(2)} €
              </Descriptions.Item>
            )}
            {detail.isAuditBooking && (
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_DETAIL_AUDIT_BOOKING_LABEL)}>
                <IconCheck size={16} color={token.colorSuccess} />
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Drawer>

      <BookingMoneyDrawer bookingId={moneyBookingId} onClose={() => setMoneyBookingId(undefined)} />

      <AdminReasonModal
        open={cancelTargetId !== undefined}
        title={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_FORCE_CANCEL_MODAL_TITLE)}
        confirmLabel={translate(t, TRANSLATIONS.ADMIN_BOOKINGS_FORCE_CANCEL_BUTTON)}
        onCancel={() => setCancelTargetId(undefined)}
        onConfirm={handleForceCancel}
      />
    </div>
  );
};

export default BookingsSection;
